import { initializeApp, type FirebaseApp } from "firebase/app";
import {
  confirmPasswordReset,
  createUserWithEmailAndPassword,
  EmailAuthProvider,
  getAuth,
  isSignInWithEmailLink,
  linkWithCredential,
  onAuthStateChanged,
  sendPasswordResetEmail,
  sendSignInLinkToEmail,
  signInAnonymously,
  signInWithEmailAndPassword,
  signInWithEmailLink,
  signOut,
  verifyPasswordResetCode,
  type ActionCodeSettings,
  type Auth,
  type User
} from "firebase/auth";

const emailLinkEmailStorageKey = "screenshot-review.email-link-email";
const emailLinkSearchParams = ["apiKey", "oobCode", "mode", "continueUrl", "lang", "tenantId"];
const firebasePasswordResetMode = "resetPassword";

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID ?? "screenshot-review",
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID
};
const firebaseEmailLinkDomain = import.meta.env.VITE_FIREBASE_EMAIL_LINK_DOMAIN?.trim();

let app: FirebaseApp | null = null;
let auth: Auth | null = null;

export interface FirebaseAuthResult {
  user: User;
  linkedAnonymousAccount: boolean;
  previousAnonymousIdToken?: string | null;
}

export function isFirebaseConfigured(): boolean {
  return Boolean(firebaseConfig.apiKey && firebaseConfig.authDomain && firebaseConfig.appId);
}

export function getFirebaseAuth(): Auth {
  if (!isFirebaseConfigured()) {
    throw new Error("Firebase frontend config is missing.");
  }

  if (!app) {
    app = initializeApp(firebaseConfig);
    auth = getAuth(app);
  }

  return auth as Auth;
}

export function subscribeToFirebaseUser(callback: (user: User | null) => void): () => void {
  return onAuthStateChanged(getFirebaseAuth(), callback);
}

export async function signInWithEmailPassword(email: string, password: string): Promise<FirebaseAuthResult> {
  try {
    const previousAnonymousIdToken = await getCurrentAnonymousIdToken();
    const result = await signInWithEmailAndPassword(getFirebaseAuth(), email, password);
    return {
      user: result.user,
      linkedAnonymousAccount: false,
      previousAnonymousIdToken
    };
  } catch (error) {
    throw mapFirebaseAuthError(error);
  }
}

export async function createEmailPasswordAccount(email: string, password: string): Promise<FirebaseAuthResult> {
  try {
    const currentUser = getFirebaseAuth().currentUser;
    if (currentUser?.isAnonymous) {
      const result = await linkWithCredential(currentUser, EmailAuthProvider.credential(email, password));
      return {
        user: result.user,
        linkedAnonymousAccount: true
      };
    }
    const result = await createUserWithEmailAndPassword(getFirebaseAuth(), email, password);
    return {
      user: result.user,
      linkedAnonymousAccount: false
    };
  } catch (error) {
    throw mapFirebaseAuthError(error);
  }
}

export async function signInAnonymouslyForReview(): Promise<User> {
  try {
    const auth = getFirebaseAuth();
    if (auth.currentUser) {
      return auth.currentUser;
    }
    const result = await signInAnonymously(auth);
    return result.user;
  } catch (error) {
    throw mapFirebaseAuthError(error);
  }
}

export function isFirebaseEmailSignInLink(url: string = window.location.href): boolean {
  if (!isFirebaseConfigured()) {
    return false;
  }
  return isSignInWithEmailLink(getFirebaseAuth(), url);
}

export function isFirebasePasswordResetLink(url: string = window.location.href): boolean {
  return getFirebaseActionCode(url, firebasePasswordResetMode) !== null;
}

export async function sendFirebaseEmailSignInLink(email: string): Promise<void> {
  try {
    const actionCodeSettings: ActionCodeSettings = {
      url: createEmailLinkReturnUrl(),
      handleCodeInApp: true
    };
    if (firebaseEmailLinkDomain) {
      actionCodeSettings.linkDomain = firebaseEmailLinkDomain;
    }
    await sendSignInLinkToEmail(getFirebaseAuth(), email, actionCodeSettings);
    window.localStorage.setItem(emailLinkEmailStorageKey, email);
  } catch (error) {
    throw mapFirebaseAuthError(error);
  }
}

export async function completeFirebaseEmailSignInLink(email: string, url: string = window.location.href): Promise<FirebaseAuthResult> {
  try {
    const auth = getFirebaseAuth();
    const currentUser = auth.currentUser;
    if (currentUser?.isAnonymous) {
      const previousAnonymousIdToken = await currentUser.getIdToken(true);
      const credential = EmailAuthProvider.credentialWithLink(email, url);
      try {
        const result = await linkWithCredential(currentUser, credential);
        clearStoredFirebaseEmailLinkEmail();
        clearFirebaseEmailLinkFromUrl();
        return {
          user: result.user,
          linkedAnonymousAccount: true
        };
      } catch (linkError) {
        if (!isExistingCredentialError(linkError)) {
          throw linkError;
        }
        const result = await signInWithEmailLink(auth, email, url);
        clearStoredFirebaseEmailLinkEmail();
        clearFirebaseEmailLinkFromUrl();
        return {
          user: result.user,
          linkedAnonymousAccount: false,
          previousAnonymousIdToken
        };
      }
    }
    const result = await signInWithEmailLink(auth, email, url);
    clearStoredFirebaseEmailLinkEmail();
    clearFirebaseEmailLinkFromUrl();
    return {
      user: result.user,
      linkedAnonymousAccount: false
    };
  } catch (error) {
    throw mapFirebaseAuthError(error);
  }
}

export function getStoredFirebaseEmailLinkEmail(): string | null {
  return window.localStorage.getItem(emailLinkEmailStorageKey);
}

export function clearStoredFirebaseEmailLinkEmail() {
  window.localStorage.removeItem(emailLinkEmailStorageKey);
}

export function clearFirebaseAuthActionFromUrl() {
  clearFirebaseEmailLinkFromUrl();
}

export async function sendFirebasePasswordReset(email: string): Promise<void> {
  try {
    const actionCodeSettings: ActionCodeSettings = {
      url: createEmailLinkReturnUrl()
    };
    if (firebaseEmailLinkDomain) {
      actionCodeSettings.linkDomain = firebaseEmailLinkDomain;
    }
    await sendPasswordResetEmail(getFirebaseAuth(), email, actionCodeSettings);
  } catch (error) {
    throw mapFirebaseAuthError(error);
  }
}

export async function getFirebasePasswordResetEmail(url: string = window.location.href): Promise<string> {
  try {
    const code = requireFirebaseActionCode(url, firebasePasswordResetMode);
    return await verifyPasswordResetCode(getFirebaseAuth(), code);
  } catch (error) {
    throw mapFirebaseAuthError(error);
  }
}

export async function completeFirebasePasswordReset(newPassword: string, url: string = window.location.href): Promise<void> {
  try {
    const code = requireFirebaseActionCode(url, firebasePasswordResetMode);
    await confirmPasswordReset(getFirebaseAuth(), code, newPassword);
    clearFirebaseEmailLinkFromUrl();
  } catch (error) {
    throw mapFirebaseAuthError(error);
  }
}

export async function signOutOfFirebase(): Promise<void> {
  await signOut(getFirebaseAuth());
}

export function isCurrentFirebaseUserAnonymous(): boolean {
  return getFirebaseAuth().currentUser?.isAnonymous ?? false;
}

async function getCurrentAnonymousIdToken(): Promise<string | null> {
  const currentUser = getFirebaseAuth().currentUser;
  return currentUser?.isAnonymous ? currentUser.getIdToken(true) : null;
}

function createEmailLinkReturnUrl(): string {
  const url = new URL(window.location.href);
  clearEmailLinkSearchParams(url);
  return `${url.origin}${url.pathname}${url.search}${url.hash}`;
}

function clearFirebaseEmailLinkFromUrl() {
  const url = new URL(window.location.href);
  clearEmailLinkSearchParams(url);
  window.history.replaceState(window.history.state, "", `${url.pathname}${url.search}${url.hash}`);
}

function clearEmailLinkSearchParams(url: URL) {
  for (const param of emailLinkSearchParams) {
    url.searchParams.delete(param);
  }
}

function getFirebaseActionCode(url: string, expectedMode: string): string | null {
  try {
    const parsedUrl = new URL(url);
    if (parsedUrl.searchParams.get("mode") !== expectedMode) {
      return null;
    }
    const code = parsedUrl.searchParams.get("oobCode")?.trim();
    return code ? code : null;
  } catch {
    return null;
  }
}

function requireFirebaseActionCode(url: string, expectedMode: string): string {
  const code = getFirebaseActionCode(url, expectedMode);
  if (!code) {
    throw new Error("This password reset link is missing a reset code.");
  }
  return code;
}

function mapFirebaseAuthError(error: unknown): Error {
  const code = typeof error === "object" && error !== null && "code" in error
    ? String((error as { code?: unknown }).code)
    : "";
  const message = (() => {
    switch (code) {
      case "auth/email-already-in-use":
      case "auth/credential-already-in-use":
        return "An account already exists for that email.";
      case "auth/invalid-credential":
      case "auth/user-not-found":
      case "auth/wrong-password":
        return "The email or password is incorrect.";
      case "auth/invalid-email":
        return "Enter a valid email address.";
      case "auth/operation-not-allowed":
        return "This sign-in method is not enabled for this Firebase project.";
      case "auth/expired-action-code":
      case "auth/invalid-action-code":
        return "This link has expired. Request a new one.";
      case "auth/missing-email":
        return "Enter the email address used to request this sign-in link.";
      case "auth/weak-password":
        return "Use a password with at least 6 characters.";
      case "auth/too-many-requests":
        return "Too many attempts. Wait a moment and try again.";
      default:
        return error instanceof Error ? error.message : "Firebase authentication failed.";
    }
  })();
  return new Error(message);
}

function isExistingCredentialError(error: unknown): boolean {
  const code = typeof error === "object" && error !== null && "code" in error
    ? String((error as { code?: unknown }).code)
    : "";
  return code === "auth/email-already-in-use" || code === "auth/credential-already-in-use";
}
